import React, {useMemo, useState} from 'react';
import {uniqueId} from 'lodash';
import {useIntl, FormattedMessage} from 'react-intl';
import Styled from 'styled-components/macro';
import {Button, Icon, Modal, TableBar} from '@forecast-it/design-system';
import {ClientContactNode, PersonProjectAccess, ViewerProjectNode} from './CompanyViewPage';
import DropdownMenu from '../shared/components/dropdown-menu/DropdownMenu';
import Util from '../shared/util/util';
import DeleteProjectPersonMutation from '../../mutations/delete_project_person_mutation';
import DeleteClientContactMutation from '../../mutations/DeleteClientContactMutation';
import ClientContactModalContainer from '../admin-tab/clients-page/ClientContactModalContainer';
import EditClientContactModalContainer from '../admin-tab/clients-page/EditClientContactModalContainer';
import {MODAL_TYPE, showModal} from '../shared/components/modals/generic_modal_conductor';
import {ConfirmationModalPrimaryButton} from '../shared/components/modals/general/ConfirmationModal';
import {SOCKET_ACTION} from '../../constants';
import {createToast} from '../shared/components/toasts/another-toast/toaster';
import {sortClientContacts} from './sortClientContacts';
import {useContactSortDirection} from './useContactSortDirection';
import ForecastTooltip from '../shared/components/tooltips/ForecastTooltip';

// ─── Styled components ───────────────────────────────────────────────────────

const PanelWrapper = Styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0 32px;
`;

const ToolbarWrapper = Styled.div`
  padding: 0 24px 0 32px;
`;

const SegmentedControlWrapper = Styled.div`
  display: inline-flex;
  border: 1px solid #6b6d7b;
  border-radius: 8px;
  background: #f5f3f9;
  padding: 4px;
`;

const SegmentButton = Styled.button<{$active: boolean}>`
  width: 80px;
  padding: 4px 10px;
  border-radius: 4px;
  border: ${({$active}) => ($active ? '1px solid #6b6d7b' : 'none')};
  background: ${({$active}) => ($active ? '#ffffff' : 'transparent')};
  ${props => props.theme.sys.font.body['1']};
  color: #232334;
  cursor: pointer;
  text-align: center;
`;

const ContentArea = Styled.div`
  padding: 16px 32px 0;
  flex: 1;
`;

const EmptyStateBox = Styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #d3d3df;
  border-radius: 8px;
  padding: 64px 0;
  background: #ffffff;
`;

const EmptyStateText = Styled.p`
  ${props => props.theme.sys.font.body['1']};
  color: #000000;
  text-align: center;
  margin: 0;
`;

const CreateContactLink = Styled.button`
  background: none;
  border: none;
  padding: 10px 12px;
  ${props => props.theme.sys.font.label['1']};
  color: #0004db;
  cursor: pointer;
  &:disabled {
    color: #a1a1ad;
    cursor: default;
  }
`;

const ContactTable = Styled.div`
  width: 100%;
`;

const TableRow = Styled.div`
  display: flex;
  align-items: center;
  height: 60px;
  border-bottom: 1px solid #d3d3df;
  padding: 0 8px;
`;

const NameCell = Styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding-left: 24px;
`;

const Avatar = Styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 4px;
  background-color: #0004db;
  ${props => props.theme.sys.font.body['2']};
  text-transform: uppercase;
  color: #ffffff;
`;

const ContactName = Styled.span`
  ${props => props.theme.sys.font.body['2']};
  color: #232334;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TextCell = Styled.div`
  flex: 1;
  min-width: 0;
  padding: 0 8px;
  ${props => props.theme.sys.font.body['2']};
  color: #232334;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GuestCell = Styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px;
  ${props => props.theme.sys.font.body['2']};
  color: #232334;
  overflow: visible;
`;

const ProjectBadge = Styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  border: 1px solid #6b6d7b;
  background: #ffffff;
  ${props => props.theme.sys.font.detail['2']};
  color: #232334;
  cursor: pointer;
  position: relative;
`;

const ProjectTooltip = Styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  left: auto;
  z-index: 100;
  background: #ffffff;
  border: 1px solid #d3d3df;
  border-radius: 8px;
  padding: 20px 24px 24px;
  width: 480px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
`;

const TooltipHeader = Styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const TooltipTitle = Styled.span`
  ${props => props.theme.sys.font.label['1']};
  color: #232334;
  white-space: nowrap;
`;

// Renders a "✕" glyph, so it still needs the themed family. The glyph size and its line
// box are both set locally because no type token is sized for an icon: body/1 carries a
// 24px line-height, which on a button that never declared one would grow it — and the
// header it sits in centres on the tallest child.
const TooltipCloseButton = Styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #232334;
  ${props => props.theme.sys.font.body['1']};
  font-size: 16px;
  line-height: normal;
`;

const TooltipTable = Styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
`;

const TooltipTableRow = Styled.div<{$last?: boolean}>`
  display: contents;
  & > * {
    border-bottom: ${({$last}) => ($last ? 'none' : '1px solid #d3d3df')};
  }
`;

const TooltipHeaderRow = Styled.div`
  display: contents;
  & > * {
    border-bottom: 1px solid #d3d3df;
  }
`;

const TooltipColId = Styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  ${props => props.theme.sys.font.subheading['2']};
  color: #232334;
`;

const TooltipColTitle = Styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  ${props => props.theme.sys.font.subheading['2']};
  color: #232334;
`;

const TooltipColDate = Styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  white-space: nowrap;
  ${props => props.theme.sys.font.subheading['2']};
  color: #232334;
`;

const TooltipColAction = Styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px;
`;

const TooltipDataColId = Styled(TooltipColId)`
  font-weight: 350;
  padding: 16px 8px;
`;

const TooltipDataColTitle = Styled(TooltipColTitle)`
  font-weight: 350;
  padding: 16px 8px;
  overflow: hidden;
`;

const TooltipDataColDate = Styled(TooltipColDate)`
  font-weight: 350;
  padding: 16px 8px;
`;

const PROJECT_ID_COLORS = [
	'#ff8fd0',
	'#27bb8f',
	'#4a90d9',
	'#f5a623',
	'#7b68ee',
	'#50c878',
	'#ff6b6b',
	'#ffd700',
	'#20b2aa',
	'#ff7f50',
];

const ProjectIdBadge = Styled.span<{$color: string}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: ${({$color}) => $color};
  border-radius: 4px;
  ${props => props.theme.sys.font.subheading['2']};
  color: #232334;
  white-space: nowrap;
`;

const TooltipRemoveButton = Styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  /* The X's colour comes entirely from the Icon color prop at the use site, not from here: Icon
     paints its own fill and never inherits currentColor, so a color rule on this button would
     style nothing. Only cursor and hit-testing are set here. */
  &:disabled {
    cursor: default;
    /* This button is nested inside the ProjectBadge button (invalid DOM the surrounding markup
       can't currently avoid). Once disabled it no longer runs its own stopPropagation, so without
       this a browser that retargets events from a disabled control — Firefox historically does —
       would deliver the click to ProjectBadge and collapse the panel. Chromium already suppresses
       it; this makes the behaviour explicit and uniform. Hover still reaches the tooltip, which
       hangs off ForecastTooltip's wrapping <span>, exactly as DropdownMenu's DisabledTrigger does. */
    pointer-events: none;
  }
`;

const PrimaryBadgeWrapper = Styled.span`
  position: relative;
  display: inline-flex;
`;

const PrimaryBadge = Styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: #e5f4ff;
  border-radius: 16px;
  ${props => props.theme.sys.font.subheading['2']};
  color: #232334;
  white-space: nowrap;
  cursor: default;
`;

const PrimaryTooltip = Styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  z-index: 100;
  background: #ffffff;
  border: 1px solid #d3d3df;
  border-radius: 8px;
  padding: 12px 16px;
  width: 280px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
`;

const PrimaryTooltipTitle = Styled.div`
  ${props => props.theme.sys.font.subheading['2']};
  color: #232334;
  margin-bottom: 8px;
`;

const PrimaryTooltipBody = Styled.div`
  ${props => props.theme.sys.font.body['2']};
  color: #232334;
`;

const ActionsCell = Styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 160px;
  flex: none;
  padding: 0 8px;
`;

// Renders a "···" glyph, so it still needs the themed family; only the glyph size
// is set locally because no type token is sized for an icon.
const KebabButton = Styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: none;
  border: none;
  cursor: pointer;
  ${props => props.theme.sys.font.body['1']};
  font-size: 18px;
  color: #232334;
  border-radius: 8px;
  &:hover {
    background: #f5f3f9;
  }
  &:disabled {
    cursor: default;
    color: #a1a1ad;
  }
  &:disabled:hover {
    background: none;
  }
`;

const ColHeader = Styled.div`
  ${props => props.theme.sys.font.subheading['2']};
  color: #232334;
  white-space: nowrap;
`;

const NameColHeader = Styled(ColHeader)`
  flex: 1;
  min-width: 0;
  padding-left: 24px;
  /* This cell shrinks with the panel but its text cannot wrap, so without clipping its content
     escapes the column and paints over the one beside it. Every other cell in this table — the
     other header cells and the name cell in each body row — already clips. */
  overflow: hidden;
`;

const NameSortButton = Styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  /* Lets the button shrink with its cell instead of holding the cell open at its content width. */
  max-width: 100%;
  min-width: 0;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  ${props => props.theme.sys.font.subheading['2']};
  color: #232334;
`;

// The label is what gives way when the column is narrow.
const NameSortLabel = Styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

// Holds the caret at its natural size while the label truncates beside it. The caret is the only
// indication that the column is sortable and which direction it currently runs, so it is the last
// thing that should disappear.
const NameSortCaret = Styled.span`
  display: inline-flex;
  flex: none;
`;

const TableHeaderRow = Styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #d3d3df;
  padding: 20px 8px;
`;

/**
 * Text available to assistive technology but never painted. Used to give the sort button a
 * description without overriding its accessible name — see the header row below.
 *
 * Hiding has to collapse the box rather than use `display: none` or `visibility: hidden`, both of
 * which remove the node from the accessibility tree too, so an `aria-describedby` pointing at one
 * would resolve to nothing.
 */
const VisuallyHidden = Styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
	clientContacts: ClientContactNode[];
	primaryContactId: string | null;
	viewerProjects: ViewerProjectNode[];
	clientId: string;
	clientName: string;
	// Called after a contact is created or deleted so the page can refetch the contacts list.
	onContactsChanged?: () => void;
	// Ids of contacts referenced by an invoice — their Delete action is disabled. Empty unless the
	// extending_client_contacts flag + NEW_CLIENT_CONTACTS_FEATURE module are on (built by the page).
	invoicedContactIds?: Set<string>;
	// Whether the current user holds CLIENT_UPDATE, the permission the backend actually validates
	// for editing this client, computed once by the page and threaded down here rather than
	// re-derived. Gates both Create Contact entry points and the row kebab trigger — every
	// endpoint behind those validates this same permission.
	canEditClient: boolean;
	// Whether the current user holds PROJECT_PERSON_DELETE, which gates the ✕ that removes a
	// contact from a project. Threaded down from the page like canEditClient so this panel is
	// drivable from props alone, and so both gates move together if either goes server-side. Kept
	// separate from canEditClient because a custom profile can hold either without the other.
	canRemoveProjectPerson: boolean;
};

// ─── Component ───────────────────────────────────────────────────────────────

const ContactsListPanel: React.FC<Props> = ({
	clientContacts,
	primaryContactId,
	viewerProjects,
	clientId,
	clientName,
	onContactsChanged,
	invoicedContactIds,
	canEditClient,
	canRemoveProjectPerson,
}) => {
	const {formatMessage, formatDate} = useIntl();
	const [filter, setFilter] = useState<'CURRENT' | 'ALL'>('CURRENT');
	const [hoveredBadgeContactId, setHoveredBadgeContactId] = useState<string | null>(null);
	const [primaryTooltipVisible, setPrimaryTooltipVisible] = useState(false);
	const [editingClientContactId, setEditingClientContactId] = useState<string | null>(null);
	const [creatingContact, setCreatingContact] = useState(false);
	const {ascending: sortAscending, toggle: toggleSortDirection} = useContactSortDirection();
	// Generated per instance rather than hard-coded: aria-describedby resolves by document id, so
	// two panels on one page sharing a literal id would point every sort button at the first hint.
	// React 16 has no useId.
	const sortHintId = useMemo(() => uniqueId('contacts-sort-name-hint-'), []);

	// canRemoveProjectPerson (prop) is, unlike every other gate on this page, one the backend does
	// NOT enforce. The ✕ fires DeleteProjectPersonMutation → ProjectLogic.deleteProjectPerson →
	// DELETE /project_person → ProjectPersonLogic.deleteProjectPerson, whose only guard is
	// validateRecord(), a tenant/company-ownership check. PROJECT_PERSON_DELETE is validated in
	// exactly one place in the backend — removePersonFromProjectGroup, reached only from
	// ProjectGroupPerson.java for a different feature — so this gate is currently the ONLY thing
	// standing between the user and the delete. Do not remove it believing the resolver repeats
	// it. Adding the server-side check is tracked separately.
	const noEditTooltip = formatMessage({id: 'company_view_page.no_edit_permission_tooltip'});
	const noRemoveTooltip = formatMessage({id: 'company_view_page.no_remove_project_person_permission_tooltip'});

	const handleEditContact = (contact: ClientContactNode) => {
		setEditingClientContactId(contact.id);
	};

	const handleDeleteContact = (contact: ClientContactNode) => {
		const contactName = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
		showModal({
			type: MODAL_TYPE.CONFIRMATION,
			title: formatMessage({id: 'contacts_panel.delete_contact_modal_title'}),
			description: (
				<div style={{whiteSpace: 'pre-line'}}>
					<FormattedMessage
						id="contacts_panel.delete_contact_modal_description"
						values={{
							contactName,
							confirmText: SOCKET_ACTION.DELETE,
							b: (chunks: React.ReactNode) => <b>{chunks}</b>,
						}}
					/>
				</div>
			),
			modalSize: Modal.SIZE.MEDIUM,
			confirmPhrase: SOCKET_ACTION.DELETE,
			includeDefaultMessage: false,
			confirmationModalPrimaryButton: ConfirmationModalPrimaryButton.DELETE,
			callbackPositive: () => {
				Util.CommitMutation(DeleteClientContactMutation, {id: contact.id, clientId}, () => {
					createToast({duration: 5000, message: formatMessage({id: 'contacts_panel.contact_deleted_toast'})});
					onContactsChanged?.();
				});
			},
		});
	};

	const visibleContacts = useMemo(
		() => (filter === 'CURRENT' ? clientContacts.filter(c => c.status === 'ACTIVE') : clientContacts),
		[clientContacts, filter]
	);

	// Layered on the filter's output, so the sort always covers whatever Current/All left
	// visible rather than fighting it.
	//
	// Entirely client-side, over the hard-capped set of contacts the page query fetches with no
	// pagination. That cap is unlikely enough to hit on one client not to fix here, but past it
	// the header would misrepresent — a contact that sorts first but arrives past the cap simply
	// is not in the list. Whoever adds pagination to this panel must move the sort server-side
	// at the same time.
	const sortedContacts = useMemo(() => sortClientContacts(visibleContacts, sortAscending), [visibleContacts, sortAscending]);

	const personIdToProjectsMap = useMemo(() => {
		const map = new Map<string, PersonProjectAccess[]>();
		viewerProjects.forEach(project => {
			project.projectPersons?.edges?.forEach(pp => {
				const node = pp?.node;
				if (!node?.person?.id) return;
				const existing = map.get(node.person.id) ?? [];
				existing.push({
					personProjectId: node.id,
					projectId: project.id,
					companyProjectId: project.companyProjectId,
					projectName: project.name ?? '',
					projectEndYear: project.projectEndYear,
					projectEndMonth: project.projectEndMonth,
					projectEndDay: project.projectEndDay,
				});
				map.set(node.person.id, existing);
			});
		});
		return map;
	}, [viewerProjects]);
	const formatProjectDueDate = (year: number | null, month: number | null, day: number | null): string => {
		if (!year || !month || !day) return '';
		return formatDate(new Date(year, month - 1, day), {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	};

	return (
		<PanelWrapper>
			<ToolbarWrapper>
				<TableBar title={formatMessage({id: 'contacts_panel.title'})}>
					<SegmentedControlWrapper>
						<SegmentButton $active={filter === 'CURRENT'} onClick={() => setFilter('CURRENT')}>
							{formatMessage({id: 'contacts_panel.filter_current'})}
						</SegmentButton>
						<SegmentButton $active={filter === 'ALL'} onClick={() => setFilter('ALL')}>
							{formatMessage({id: 'contacts_panel.filter_all'})}
						</SegmentButton>
					</SegmentedControlWrapper>
					<ForecastTooltip content={canEditClient ? undefined : noEditTooltip} spanStyle={{display: 'inline-flex'}}>
						<Button emphasis="medium" disabled={!canEditClient} onPress={() => setCreatingContact(true)}>
							{formatMessage({id: 'contacts_panel.create_contact'})}
						</Button>
					</ForecastTooltip>
				</TableBar>
			</ToolbarWrapper>
			<ContentArea>
				{visibleContacts.length === 0 ? (
					<EmptyStateBox>
						<EmptyStateText>{formatMessage({id: 'contacts_panel.empty_state'})}</EmptyStateText>
						<ForecastTooltip
							content={canEditClient ? undefined : noEditTooltip}
							spanStyle={{display: 'inline-flex'}}
						>
							<CreateContactLink disabled={!canEditClient} onClick={() => setCreatingContact(true)}>
								{formatMessage({id: 'contacts_panel.create_contact'})}
							</CreateContactLink>
						</ForecastTooltip>
					</EmptyStateBox>
				) : (
					// This is a flexbox table, not a <table>, so the grid roles have to be declared
					// explicitly. They are load-bearing, not decoration: aria-sort below is only
					// honoured on a columnheader, and a columnheader is only valid inside a row
					// inside a table. Without the full chain the sort state is dropped from the
					// accessibility tree entirely and the header surfaces as loose text. Keep the
					// role on every cell — a row that is missing some leaves an uneven grid.
					<ContactTable role="table">
						{/* Header row */}
						<TableHeaderRow role="row">
							<NameColHeader role="columnheader" aria-sort={sortAscending ? 'ascending' : 'descending'}>
								{/*
								  No aria-label here. One would become the button's accessible name
								  and shadow the visible "Contact Name" text, which breaks WCAG 2.5.3
								  (Label in Name): a voice-control user saying "click Contact Name"
								  would match nothing. The visible label stays the accessible name and
								  the action rides along as a description instead, which screen
								  readers announce after it.
								*/}
								<NameSortButton
									type="button"
									data-testid="contacts-sort-name"
									aria-describedby={sortHintId}
									onClick={toggleSortDirection}
								>
									<NameSortLabel data-testid="contacts-sort-name-label">
										{formatMessage({id: 'contacts_panel.col_name'})}
									</NameSortLabel>
									<NameSortCaret data-testid="contacts-sort-caret" aria-hidden="true">
										<Icon icon={sortAscending ? 'caretUp' : 'caretDown'} size="s" />
									</NameSortCaret>
								</NameSortButton>
								<VisuallyHidden id={sortHintId} data-testid="contacts-sort-name-hint">
									{formatMessage({
										id: sortAscending ? 'contacts_panel.sort_name_desc' : 'contacts_panel.sort_name_asc',
									})}
								</VisuallyHidden>
							</NameColHeader>
							<TextCell role="columnheader" style={{flex: 1}}>
								{formatMessage({id: 'contacts_panel.col_email'})}
							</TextCell>
							<TextCell role="columnheader" style={{flex: 1}}>
								{formatMessage({id: 'contacts_panel.col_phone'})}
							</TextCell>
							<TextCell role="columnheader" style={{flex: 1}}>
								{formatMessage({id: 'contacts_panel.col_position'})}
							</TextCell>
							<TextCell role="columnheader" style={{width: 80, flex: 'none' as const}}>
								{formatMessage({id: 'contacts_panel.col_status'})}
							</TextCell>
							<TextCell role="columnheader" style={{width: 120, flex: 'none' as const}}>
								{formatMessage({id: 'contacts_panel.col_guest_user'})}
							</TextCell>
							<TextCell role="columnheader" style={{width: 160, flex: 'none' as const}} />
						</TableHeaderRow>

						{sortedContacts.map(contact => {
							const initials = `${contact.firstName?.[0] ?? ''}${contact.lastName?.[0] ?? ''}`.toUpperCase();
							const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
							const projectAccesses = contact.person?.id
								? personIdToProjectsMap.get(contact.person.id) ?? []
								: [];
							const statusLabel =
								contact.status === 'ACTIVE'
									? 'Active'
									: contact.status === 'INACTIVE'
									? 'Inactive'
									: contact.status ?? '-';

							return (
								<TableRow key={contact.id} role="row">
									<NameCell role="cell" style={{flex: 1, minWidth: 0}}>
										<Avatar>{initials}</Avatar>
										<ContactName data-testid="contact-name">{fullName}</ContactName>
									</NameCell>
									<TextCell role="cell" style={{flex: 1}}>
										{contact.email ?? '-'}
									</TextCell>
									<TextCell role="cell" style={{flex: 1}}>
										{contact.phone ?? '-'}
									</TextCell>
									<TextCell role="cell" style={{flex: 1}}>
										{contact.jobTitle ?? '-'}
									</TextCell>
									<TextCell role="cell" style={{width: 80, flex: 'none' as const}}>
										{statusLabel}
									</TextCell>
									<GuestCell role="cell" style={{width: 120, flex: 'none' as const, position: 'relative'}}>
										{contact.isGuestUser ? 'Yes' : 'No'}
										{contact.isGuestUser && projectAccesses.length > 0 && (
											<ProjectBadge
												data-testid="project-count-badge"
												onClick={e => {
													e.stopPropagation();
													setHoveredBadgeContactId(prev => (prev === contact.id ? null : contact.id));
												}}
											>
												{projectAccesses.length}
												{hoveredBadgeContactId === contact.id && (
													<ProjectTooltip>
														<TooltipHeader>
															<TooltipTitle>
																{formatMessage({
																	id: 'contacts_panel.tooltip_guest_access_title',
																})}
															</TooltipTitle>
															<TooltipCloseButton
																onClick={e => {
																	e.stopPropagation();
																	setHoveredBadgeContactId(null);
																}}
																aria-label="Close"
															>
																✕
															</TooltipCloseButton>
														</TooltipHeader>
														<TooltipTable>
															<TooltipHeaderRow>
																<TooltipColId>
																	{formatMessage({id: 'contacts_panel.tooltip_col_id'})}
																</TooltipColId>
																<TooltipColTitle>
																	{formatMessage({
																		id: 'contacts_panel.tooltip_col_project_title',
																	})}
																</TooltipColTitle>
																<TooltipColDate>
																	{formatMessage({id: 'contacts_panel.tooltip_col_due_date'})}
																</TooltipColDate>
																<TooltipColAction />
															</TooltipHeaderRow>
															{projectAccesses.map((pa, i) => (
																<TooltipTableRow
																	key={pa.personProjectId}
																	$last={i === projectAccesses.length - 1}
																>
																	<TooltipDataColId>
																		<ProjectIdBadge
																			$color={
																				PROJECT_ID_COLORS[i % PROJECT_ID_COLORS.length]
																			}
																		>
																			{pa.companyProjectId != null
																				? `P${pa.companyProjectId}`
																				: '—'}
																		</ProjectIdBadge>
																	</TooltipDataColId>
																	<TooltipDataColTitle>
																		<span
																			style={{
																				overflow: 'hidden',
																				textOverflow: 'ellipsis',
																				whiteSpace: 'nowrap',
																			}}
																		>
																			{pa.projectName}
																		</span>
																	</TooltipDataColTitle>
																	<TooltipDataColDate>
																		{formatProjectDueDate(
																			pa.projectEndYear,
																			pa.projectEndMonth,
																			pa.projectEndDay
																		)}
																	</TooltipDataColDate>
																	<TooltipColAction>
																		<ForecastTooltip
																			content={
																				canRemoveProjectPerson
																					? undefined
																					: noRemoveTooltip
																			}
																			spanStyle={{display: 'inline-flex'}}
																		>
																			<TooltipRemoveButton
																				disabled={!canRemoveProjectPerson}
																				onClick={e => {
																					e.stopPropagation();
																					Util.CommitMutation(
																						DeleteProjectPersonMutation,
																						{
																							ids: [pa.personProjectId],
																							projectId: pa.projectId,
																						},
																						() => setHoveredBadgeContactId(null)
																					);
																				}}
																				data-testid="remove-from-project"
																				aria-label="Remove from project"
																			>
																				<Icon
																					icon="closeCircle"
																					size="m"
																					color={
																						canRemoveProjectPerson
																							? 'default'
																							: 'disabled'
																					}
																				/>
																			</TooltipRemoveButton>
																		</ForecastTooltip>
																	</TooltipColAction>
																</TooltipTableRow>
															))}
														</TooltipTable>
													</ProjectTooltip>
												)}
											</ProjectBadge>
										)}
									</GuestCell>
									<ActionsCell role="cell">
										{contact.id === primaryContactId && (
											<PrimaryBadgeWrapper>
												<PrimaryBadge
													data-testid="primary-badge"
													onMouseEnter={() => setPrimaryTooltipVisible(true)}
													onMouseLeave={() => setPrimaryTooltipVisible(false)}
												>
													{formatMessage({id: 'contacts_panel.badge_primary'})}
												</PrimaryBadge>
												{primaryTooltipVisible && (
													<PrimaryTooltip data-testid="primary-badge-tooltip">
														<PrimaryTooltipTitle>
															{formatMessage({id: 'contacts_panel.badge_primary_tooltip_title'})}
														</PrimaryTooltipTitle>
														<PrimaryTooltipBody>
															{formatMessage({id: 'contacts_panel.badge_primary_tooltip_body'})}
														</PrimaryTooltipBody>
													</PrimaryTooltip>
												)}
											</PrimaryBadgeWrapper>
										)}
										<DropdownMenu
											disabled={!canEditClient}
											disabledTooltip={noEditTooltip}
											trigger={<KebabButton data-testid={`kebab-menu-${contact.id}`}>···</KebabButton>}
										>
											<DropdownMenu.Item onClick={() => handleEditContact(contact)}>
												{formatMessage({id: 'contacts_panel.action_edit'})}
											</DropdownMenu.Item>
											<DropdownMenu.Item
												onClick={() => handleDeleteContact(contact)}
												disabled={invoicedContactIds?.has(contact.id)}
												tooltip={
													invoicedContactIds?.has(contact.id)
														? formatMessage({id: 'contacts_panel.contact_delete_invoiced'})
														: undefined
												}
												dataCy="delete-contact"
											>
												{formatMessage({id: 'contacts_panel.action_delete'})}
											</DropdownMenu.Item>
										</DropdownMenu>
									</ActionsCell>
								</TableRow>
							);
						})}
					</ContactTable>
				)}
			</ContentArea>
			{editingClientContactId && (
				<EditClientContactModalContainer
					clientContactId={editingClientContactId}
					onClose={() => setEditingClientContactId(null)}
				/>
			)}
			{creatingContact && (
				<ClientContactModalContainer
					mode="contact"
					entryPoint="client-page"
					clientId={clientId}
					preSelectedClientName={clientName}
					onContactSaved={onContactsChanged}
					onClose={() => setCreatingContact(false)}
				/>
			)}
		</PanelWrapper>
	);
};

export default ContactsListPanel;
